import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useId } from "react";
import {
  type BalloonDock,
  type BalloonPlace,
  type BalloonRect,
  type PixelBox,
  placeIssueBalloons,
} from "@/components/audits/replay/balloonPlace";

export type { BalloonDock };

export type FrameIssueBalloon = {
  id: string;
  n: number;
  title: string;
  description: string | null;
  group: "high" | "medium" | "low";
  sevLabel: string;
  rect: BalloonRect;
  dock?: BalloonDock;
};

type Props = {
  issues: FrameIssueBalloon[];
  showNumber: boolean;
  hoveredId: string | null;
  openId: string | null;
  frameSize: { width: number; height: number };
  staticCallout?: boolean;
  expandInline?: boolean;
  dock?: BalloonDock;
  interactive?: boolean;
  onHover: (id: string | null) => void;
  onOpen: (id: string, origin: HTMLElement | null) => void;
  onBalloonNode: (id: string, node: HTMLElement | null) => void;
};

const OPEN_EASE = "duration-[280ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none";

const SPOTLIGHT_PAD_PX = 8;
const REST_DIM = 0.16;
const ACTIVE_DIM = 0.48;

export default function FrameIssueBalloons({
  issues,
  showNumber,
  hoveredId,
  openId,
  frameSize,
  staticCallout = false,
  expandInline = false,
  dock = "auto",
  interactive = !staticCallout,
  onHover,
  onOpen,
  onBalloonNode,
}: Props) {
  const maskId = useId().replaceAll(":", "");
  const activeId = hoveredId || openId;
  const holeIssues = activeId ? issues.filter((issue) => issue.id === activeId) : issues;
  const holes = holeIssues.map((issue) => ({
    id: issue.id,
    ...paddedSpotlightRect(issue.rect, frameSize),
  }));
  const radiusX = frameSize.width > 0 ? (SPOTLIGHT_PAD_PX / frameSize.width) * 100 : 0.8;
  const radiusY = frameSize.height > 0 ? (SPOTLIGHT_PAD_PX / frameSize.height) * 100 : 0.8;
  const placements = placeIssueBalloons(
    issues.map((issue) => ({
      id: issue.id,
      rect: paddedSpotlightRect(issue.rect, frameSize),
      dock: issue.dock,
    })),
    frameSize,
    dock,
  );

  return (
    <>
      {holes.length > 0 ? (
        <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden" aria-hidden="true">
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <mask id={maskId} maskUnits="userSpaceOnUse">
                <rect width="100" height="100" fill="white" />
                {holes.map((hole) => (
                  <rect
                    key={hole.id}
                    x={hole.left}
                    y={hole.top}
                    width={hole.width}
                    height={hole.height}
                    rx={radiusX}
                    ry={radiusY}
                    fill="black"
                  />
                ))}
              </mask>
            </defs>
            <rect
              className="transition-opacity duration-200 ease-out"
              width="100"
              height="100"
              fill="rgb(15 23 42)"
              style={{ opacity: activeId ? ACTIVE_DIM : REST_DIM }}
              mask={`url(#${maskId})`}
            />
          </svg>
          {holes.map((hole) => (
            <div
              key={hole.id}
              className="absolute rounded-lg border-2 border-white"
              style={{
                left: `${hole.left}%`,
                top: `${hole.top}%`,
                width: `${hole.width}%`,
                height: `${hole.height}%`,
              }}
            />
          ))}
        </div>
      ) : null}
      {issues.map((issue) => {
        const active = issue.id === activeId;
        const opened = issue.id === openId;
        const name = showNumber
          ? `Issue ${issue.n}, ${issue.sevLabel}: ${issue.title}`
          : `${issue.sevLabel}: ${issue.title}`;
        const hit = paddedSpotlightRect(issue.rect, frameSize);
        const placed = placements.get(issue.id);
        const place = placed?.place ?? { dock: "above" as const, x: "start" as const, y: "start" as const };
        const pixelPosition = balloonOffsetStyle(hit, placed?.box, frameSize, place);
        const hideForModal = opened && !expandInline;
        const inlineOpen = opened && expandInline;

        return (
          <div
            key={issue.id}
            className={`pointer-events-none absolute ${active ? "z-30" : "z-20"}`}
            style={{
              left: `${hit.left}%`,
              top: `${hit.top}%`,
              width: `${hit.width}%`,
              height: `${hit.height}%`,
            }}
          >
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              className={`pointer-events-auto absolute inset-0 ${interactive ? "cursor-pointer" : ""}`}
              onMouseEnter={() => onHover(issue.id)}
              onMouseLeave={() => onHover(null)}
              onClick={interactive ? (event) => onOpen(issue.id, event.currentTarget) : undefined}
            />
            <div
              className={`absolute z-2 flex flex-col p-1.5 ${pixelPosition ? "" : dockClass(place)} ${hideForModal ? "invisible" : ""}`}
              style={pixelPosition}
            >
              <button
                type="button"
                ref={(node) => onBalloonNode(issue.id, node)}
                title={balloonActionLabel(name, interactive, staticCallout, inlineOpen)}
                aria-label={balloonActionLabel(name, interactive, staticCallout, inlineOpen)}
                aria-expanded={expandInline ? inlineOpen : undefined}
                className={`pointer-events-auto relative flex flex-col items-start gap-1 rounded-lg border bg-white px-2.5 py-1.5 text-left font-sans text-gray-900 ${OPEN_EASE} transition-shadow ${
                  expandInline ? "w-70 max-sm:w-55" : "min-w-58 max-w-70 max-sm:min-w-45 max-sm:max-w-55"
                } ${interactive ? "cursor-pointer" : ""} ${sevBorderClass(issue.group)} ${
                  opened
                    ? "shadow-[0_10px_28px_rgb(15_23_42/0.28)]"
                    : active
                      ? "shadow-[0_8px_22px_rgb(15_23_42/0.22)]"
                      : "shadow-[0_4px_14px_rgb(15_23_42/0.16)]"
                }`}
                onMouseEnter={() => onHover(issue.id)}
                onMouseLeave={() => onHover(null)}
                onClick={interactive ? (event) => onOpen(issue.id, event.currentTarget) : undefined}
              >
                <span
                  aria-hidden="true"
                  className={`absolute h-2.5 w-2.5 rotate-45 border bg-white ${sevBorderClass(issue.group)} ${pointerClass(place)}`}
                />
                <span className="flex items-center gap-1.5 leading-none">
                  {showNumber ? (
                    <span
                      className={`flex h-3 w-3 shrink-0 items-center justify-center rounded-full text-[10px] font-bold leading-none text-white ${markClass(issue.group)}`}
                    >
                      {issue.n}
                    </span>
                  ) : null}
                  <span
                    className={`flex items-center text-[10px] font-bold uppercase tracking-wider leading-none ${sevTextClass(issue.group)}`}
                  >
                    {issue.group}
                  </span>
                </span>
                <span className="block w-full min-w-0">
                  <span className="line-clamp-2 block text-sm font-semibold leading-snug">{issue.title}</span>
                  {expandInline && issue.description ? (
                    <span
                      className={`grid ${OPEN_EASE} transition-[grid-template-rows] ${
                        inlineOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                      }`}
                    >
                      <span className="min-h-0 overflow-hidden">
                        <span
                          className={`block pt-1 text-sm font-normal leading-relaxed text-gray-700 ${OPEN_EASE} transition-opacity ${
                            inlineOpen ? "opacity-100" : "opacity-0"
                          }`}
                        >
                          {issue.description}
                        </span>
                      </span>
                    </span>
                  ) : null}
                </span>
                {staticCallout ? null : (
                  <span className="inline-flex items-center gap-0.5 text-xs font-semibold leading-tight text-gray-500">
                    {inlineOpen ? "Close" : "View issue"}
                    <ChevronRightIcon
                      className={`h-3.5 w-3.5 ${OPEN_EASE} transition-transform ${inlineOpen ? "rotate-90" : ""}`}
                      aria-hidden="true"
                    />
                  </span>
                )}
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
}

function balloonActionLabel(name: string, interactive: boolean, staticCallout: boolean, opened: boolean) {
  if (!interactive) {
    return name;
  }

  if (staticCallout) {
    return `${name}. See how we found this`;
  }

  if (opened) {
    return `${name}. Close`;
  }

  return `${name}. View issue`;
}

function paddedSpotlightRect(rect: BalloonRect, frameSize: { width: number; height: number }): BalloonRect {
  const padX = frameSize.width > 0 ? (SPOTLIGHT_PAD_PX / frameSize.width) * 100 : 0;
  const padY = frameSize.height > 0 ? (SPOTLIGHT_PAD_PX / frameSize.height) * 100 : 0;
  const left = Math.max(0, rect.left - padX);
  const top = Math.max(0, rect.top - padY);
  const right = Math.min(100, rect.left + rect.width + padX);
  const bottom = Math.min(100, rect.top + rect.height + padY);

  return {
    left,
    top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
}

function balloonOffsetStyle(
  hit: BalloonRect,
  box: PixelBox | undefined,
  frameSize: { width: number; height: number },
  place: BalloonPlace,
) {
  if (!box || box.width <= 0 || box.height <= 0 || frameSize.width <= 0 || frameSize.height <= 0) {
    return undefined;
  }

  const hitLeft = (hit.left / 100) * frameSize.width;
  const hitTop = (hit.top / 100) * frameSize.height;
  const hitRight = hitLeft + (hit.width / 100) * frameSize.width;
  const hitBottom = hitTop + (hit.height / 100) * frameSize.height;
  const boxRight = box.left + box.width;
  const boxBottom = box.top + box.height;

  if (place.dock === "left" || place.dock === "right") {
    return {
      ...(place.dock === "left" ? { right: `${hitRight - boxRight}px` } : { left: `${box.left - hitLeft}px` }),
      top: `${box.top + box.height / 2 - hitTop}px`,
      transform: "translateY(-50%)",
    };
  }

  const pinRight = place.x === "end";
  const pinBottom = place.dock === "above";

  return {
    ...(pinRight ? { right: `${hitRight - boxRight}px` } : { left: `${box.left - hitLeft}px` }),
    ...(pinBottom ? { bottom: `${hitBottom - boxBottom}px` } : { top: `${box.top - hitTop}px` }),
  };
}

function dockClass(place: BalloonPlace) {
  const xClass = place.x === "end" ? "right-0" : "left-0";
  const yClass = place.y === "end" ? "bottom-0" : "top-0";

  if (place.dock === "above") return `bottom-full ${xClass}`;
  if (place.dock === "below") return `top-full ${xClass}`;
  if (place.dock === "left") return "right-full top-1/2 -translate-y-1/2";
  if (place.dock === "right") return "left-full top-1/2 -translate-y-1/2";

  return `${xClass} ${yClass}`;
}

function pointerClass(place: BalloonPlace) {
  const xClass = place.x === "end" ? "right-3.5" : "left-3.5";

  if (place.dock === "above") return `-bottom-1.5 ${xClass} border-l-0 border-t-0`;
  if (place.dock === "below") return `-top-1.5 ${xClass} border-r-0 border-b-0`;
  if (place.dock === "left") return "-right-1.5 top-1/2 -translate-y-1/2 border-b-0 border-l-0";
  if (place.dock === "right") return "-left-1.5 top-1/2 -translate-y-1/2 border-r-0 border-t-0";

  return `-bottom-1.5 ${xClass} border-l-0 border-t-0`;
}

function markClass(group: FrameIssueBalloon["group"]) {
  if (group === "high") return "bg-error";
  if (group === "medium") return "bg-warning";

  return "bg-primary";
}

function sevTextClass(group: FrameIssueBalloon["group"]) {
  if (group === "high") return "text-error-dark";
  if (group === "medium") return "text-warning-dark";

  return "text-primary";
}

function sevBorderClass(group: FrameIssueBalloon["group"]) {
  if (group === "high") return "border-error";
  if (group === "medium") return "border-warning";

  return "border-primary";
}
